import type {InfinityClient} from '@pexip/infinity';
import {ClientCallType} from '@pexip/infinity/clientCallType';
import {buildUrl, type Url} from '@pexip/router';
import {formatPath} from '@pexip/router/utils';

import {config} from '../config';
import {
    EXPRESS_PATH,
    MEETING_MANAGER_PATH,
    STEP_BY_STEP_PATH,
} from '../constants';
import {logger} from '../logger';
import {parseMeetingEntryPath, replace, router} from '../router';

import {
    MEETING_LINK_QUERY_NAMES,
    type MeetingLinkParams,
    parseMeetingLinkParams,
} from './parseMeetingLinkParams';

const updateConfig: typeof config.set = opt => {
    logger.debug(
        {
            previous: {[opt.key]: config.get(opt.key)},
            next: {[opt.key]: opt.value},
            persist: Boolean(opt.persist),
        },
        'Update config',
    );
    return config.set(opt);
};

/**
 * Builds the meeting entry route for a meeting link. The alias is
 * percent-encoded so that it always stays a single path segment.
 *
 * @param conferenceAlias - Alias of the conference to open.
 * @param params - Parsed meeting link settings.
 * @returns A route relative to the application base.
 *
 * @example
 * getMeetingEntryPath('meet/alice', {role: 'guest', language: 'en'});
 * // '/m/meet%2Falice/step-by-step?lng=en'
 */
export const getMeetingEntryPath = (
    conferenceAlias: string,
    {flow, join, role, language, rid}: MeetingLinkParams,
): string => {
    let path = formatPath`/m/${conferenceAlias}`;
    if (flow === MEETING_MANAGER_PATH) {
        path += `/${MEETING_MANAGER_PATH}`;
    } else if (!join) {
        path += `/${flow ?? (role === 'guest' ? STEP_BY_STEP_PATH : EXPRESS_PATH)}`;
    }

    const query = new URLSearchParams();
    if (language) {
        // Read by i18next's query string language detection.
        query.set(MEETING_LINK_QUERY_NAMES.language, language);
    }
    if (rid) {
        query.set(MEETING_LINK_QUERY_NAMES.rid, rid);
    }
    const search = query.toString();
    return search ? `${path}?${search}` : path;
};

/**
 * Applies parsed meeting link settings to the configuration and the Infinity
 * client, and opens the meeting entry route when the link names a conference.
 *
 * @param infinityClient - Client receiving the call credentials.
 * @param params - Settings returned by `parseMeetingLinkParams`.
 *
 * @example
 * applyMeetingLinkParams(
 *     infinityClient,
 *     parseMeetingLinkParams(new URLSearchParams('conference=meet&join=1')),
 * );
 */
export const applyMeetingLinkParams = (
    infinityClient: InfinityClient,
    params: MeetingLinkParams,
): void => {
    // The meeting manager joins directly, without media, and accepts no
    // other call type.
    const isMeetingManager = params.flow === MEETING_MANAGER_PATH;

    updateConfig({key: 'isGuest', value: params.role === 'guest'});

    if (params.displayName) {
        updateConfig({key: 'displayName', value: params.displayName});
        updateConfig({key: 'skipDisplayNamePrompt', value: true});
    }

    if (params.bandwidth) {
        updateConfig({key: 'bandwidth', value: params.bandwidth});
    }

    if (isMeetingManager) {
        updateConfig({key: 'callType', value: ClientCallType.None});
    } else if (params.callType !== undefined) {
        updateConfig({key: 'callType', value: params.callType});
    }

    if (params.allowChangeOfMediaDirection !== undefined) {
        updateConfig({
            key: 'allowChangeOfMediaDirection',
            value: {
                audio: params.allowChangeOfMediaDirection,
                video: params.allowChangeOfMediaDirection,
                presentation: true,
            },
        });
    } else if (params.callType !== undefined) {
        // A link that sets a call type locks audio and video directions
        // unless it explicitly allows changing them.
        updateConfig({
            key: 'allowChangeOfMediaDirection',
            value: {audio: false, video: false, presentation: true},
        });
    }

    // Enforced mute wins; useEnforceMuteByDefault applies it.
    if (params.muteCamera !== undefined && !config.get('enforceVideoMute')) {
        updateConfig({key: 'isVideoInputMuted', value: params.muteCamera});
    }
    if (
        params.muteMicrophone !== undefined &&
        !config.get('enforceAudioMute')
    ) {
        updateConfig({key: 'isAudioInputMuted', value: params.muteMicrophone});
    }

    if (params.videoProcessingAPI) {
        updateConfig({
            key: 'videoProcessingAPI',
            value: params.videoProcessingAPI,
        });
    }

    if (params.ssoToken) {
        infinityClient.setSsoToken(params.ssoToken);
    }

    if (params.token) {
        infinityClient.setToken(params.token);
    }

    if (params.conferenceAlias) {
        replace(getMeetingEntryPath(params.conferenceAlias, params));
    }

    if (params.pin) {
        infinityClient.setPin(params.pin);
    }

    if (params.extension) {
        infinityClient.setConferenceExtension(params.extension);
    }

    if (params.join || isMeetingManager) {
        updateConfig({key: 'join', value: true});
    }

    if (params.callTag) {
        infinityClient.setCallTag(params.callTag);
    }
};

/**
 * Parses the meeting link at a URL as a page load at it applies it. Query
 * parameters take precedence over the path. Without a query the route is
 * already the entry route, so the path only selects the flow, such as the
 * meeting manager.
 *
 * @param url - URL of the meeting entry route.
 * @returns The link settings.
 * @throws {URIError} When the path has invalid percent-encoding.
 *
 * @example
 * parseMeetingLinkUrl(buildUrl('/m/meet.alice/express?name=Bob'));
 * // {conferenceAlias: 'meet.alice', displayName: 'Bob', flow: 'express'}
 */
export const parseMeetingLinkUrl = ({
    path,
    query,
    fragment,
}: Pick<Url, 'path' | 'query' | 'fragment'>): MeetingLinkParams => {
    const relativePath = path.slice(router.baseUri.length);
    const [pathAlias, pathFlow] = parseMeetingEntryPath(relativePath);
    // A hash route carries its query after `#/?`.
    const paramsString = query || fragment.substring(2);
    return parseMeetingLinkParams(
        new URLSearchParams(paramsString),
        paramsString ? {pathConferenceAlias: pathAlias, pathFlow} : {pathFlow},
    );
};

/**
 * Applies the meeting link in the current location, as a page load does.
 *
 * @param infinityClient - Client receiving the call credentials.
 * @throws {URIError} When the path has invalid percent-encoding.
 *
 * @example
 * // At `/m/meet.alice/express?name=Bob&pin=1234`
 * applyMeetingLinkFromLocation(infinityClient);
 */
export const applyMeetingLinkFromLocation = (
    infinityClient: InfinityClient,
): void => {
    applyMeetingLinkParams(
        infinityClient,
        parseMeetingLinkUrl(buildUrl(window.location.href)),
    );
};
